import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@sc/data-access-environment';
import { AccessToken, IDToken } from '@okta/okta-auth-js';
import {
  AuthenticationMethod,
  buildApiUrl,
  DossierToken,
  Environment,
  exchangeDossierTokenApi,
  GetDossierTokenApi,
  GetPreDeclarationTokenApi,
  OktaAccount,
  PreDeclarationToken,
} from '@sc/data-access-models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  private readonly apiUrl: string;
  private readonly apiOidcUrl: string;
  private readonly apiSamlUrl: string;
  private readonly insurerAuthURL: string;
  private readonly fitterAuthURL: string;
  private readonly accountAuthURL: string;
  private readonly refreshTokenUrl: string;
  private readonly predeclarationTokenUrl: string;
  private readonly signInUrl: string;
  private readonly resetPasswordUrl: string;
  private readonly superAdminSignInURL: string;

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(ENV_CONFIG) env: Environment
  ) {
    this.apiUrl = buildApiUrl(env.api, 'auth');
    this.apiOidcUrl = `${this.apiUrl}/oidc`;
    this.apiSamlUrl = `${this.apiUrl}/saml`;
    this.insurerAuthURL = `${this.apiUrl}/insurer`;
    this.fitterAuthURL = `${this.apiUrl}/fitter`;
    this.accountAuthURL = `${this.apiUrl}/account`;
    this.refreshTokenUrl = `${this.apiUrl}/token/refresh`;
    this.predeclarationTokenUrl = `${this.apiUrl}/start-pre-declaration`;
    this.signInUrl = `${this.apiUrl}/password`;
    this.resetPasswordUrl = `${this.apiUrl}/reset-password`;
    this.superAdminSignInURL = this.apiUrl;
  }

  getDossierToken(params: GetDossierTokenApi): Observable<DossierToken> {
    return this.httpClient.post<DossierToken>(this.apiUrl, params, {
      headers: { requestName: 'auth-http getDossierToken' },
    });
  }

  exchangeDossierToken(
    params: exchangeDossierTokenApi
  ): Observable<DossierToken> {
    return this.httpClient.post<DossierToken>(this.apiOidcUrl, params, {
      headers: { requestName: 'auth-http exchangeDossierToken' },
    });
  }

  forwardOidc(businessOrigin?: string) {
    let redirect_uri = window.location.origin;
    if (businessOrigin) {
      redirect_uri = encodeURIComponent(
        redirect_uri + '?businessOrigin=' + businessOrigin
      );
      window.location.replace(
        this.apiOidcUrl +
          '?businessOrigin=' +
          businessOrigin +
          '&redirect_uri=' +
          redirect_uri
      );
    } else {
      window.location.replace(
        this.apiOidcUrl + '?redirect_uri=' + redirect_uri
      );
    }
  }

  forwardSaml() {
    window.location.replace(this.apiSamlUrl);
  }

  getPreDeclarationToken(
    params: GetPreDeclarationTokenApi
  ): Observable<PreDeclarationToken> {
    return this.httpClient.post<PreDeclarationToken>(
      this.predeclarationTokenUrl,
      params,
      {
        headers: { requestName: 'auth-http getPreDeclarationToken' },
      }
    );
  }

  refreshAccessToken(refreshToken: string) {
    return this.httpClient.post<{
      accessToken: AccessToken;
      idToken: IDToken;
      refreshToken: string;
    }>(
      this.refreshTokenUrl,
      { refreshToken },
      {
        headers: {
          requestName: 'auth-http refreshAccessToken',
        },
      }
    );
  }

  doAuthenticationInsurer(
    email: string,
    authenticationMethod?: AuthenticationMethod,
    forgotPassword?: boolean
  ) {
    return this.httpClient.post(
      this.insurerAuthURL,
      { email, authenticationMethod, forgotPassword },
      {
        headers: { requestName: 'auth-http sendInsurerAuthEmail' },
      }
    );
  }

  doAuthenticationFitter(
    email: string,
    authenticationMethod?: AuthenticationMethod,
    forgotPassword?: boolean
  ) {
    return this.httpClient.post(
      this.fitterAuthURL,
      { email, authenticationMethod, forgotPassword },
      {
        headers: { requestName: 'auth-http sendFitterAuthEmail' },
      }
    );
  }

  signIn(email: string, password: string): Observable<Object> {
    return this.httpClient.post(
      this.signInUrl,
      { email, password },
      {
        headers: { requestName: 'auth-http signIn' },
      }
    );
  }

  superAdminSignIn(email: string, password: string): Observable<Object> {
    return this.httpClient.post(
      this.superAdminSignInURL,
      { email, password },
      {
        headers: { requestName: 'auth-http signIn' },
      }
    );
  }

  resetPassword(encryptedInfo: string, password: string): Observable<Object> {
    return this.httpClient.post(
      this.resetPasswordUrl,
      { encryptedInfo, password },
      {
        headers: { requestName: 'auth-http resetPassword' },
      }
    );
  }

  getOktaAccount(
    encryptedInfo: string
  ): Observable<{ oktaAccount: OktaAccount }> {
    return this.httpClient.post<{ oktaAccount: OktaAccount }>(
      this.accountAuthURL,
      { encryptedInfo },
      {
        headers: { requestName: 'auth-http resetPassword' },
      }
    );
  }
}
